import { type FC, type MouseEvent, useState } from "react";
import { Button, Chip, Popover, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment, { type Moment } from "moment-timezone";
import {
  PERIOD_PRESETS,
  PERIOD_PRESET_LABELS,
  customPeriod,
  presetPeriod,
  type DashboardPeriod,
  type DayRange,
  type PeriodPreset,
} from "../model";

interface PeriodPickerProps {
  period: DashboardPeriod;
  today: Date;
  onChange: (period: DashboardPeriod) => void;
}

const OPTIONS: PeriodPreset[] = [...PERIOD_PRESETS, "custom"];
const DAY_FORMAT = "YYYY-MM-DD";

/**
 * Выбор периода чипами внутри панели, как быстрые фильтры QuickFilter
 * (спека 2026-10-16 §3.1): выбранный чип залит, «Свой период» последним и
 * открывает Popover с двумя DatePicker в UTC. На телефоне чипы
 * переносятся строками, отдельного Select нет.
 */
export const PeriodPicker: FC<PeriodPickerProps> = ({
  period,
  today,
  onChange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<DayRange>(period.range);

  const close = () => setAnchor(null);

  const select = (preset: PeriodPreset, event: MouseEvent<HTMLElement>) => {
    if (preset === "custom") {
      setDraft(period.range);
      setAnchor(event.currentTarget);
      return;
    }
    if (preset === period.preset) return;
    onChange(presetPeriod(preset, today));
  };

  const changeDay = (edge: keyof DayRange) => (value: Moment | null) => {
    if (!value || !value.isValid()) return;
    setDraft((current) => ({ ...current, [edge]: value.format(DAY_FORMAT) }));
  };

  const apply = () => {
    onChange(customPeriod(draft));
    close();
  };

  return (
    <>
      <Stack
        direction="row"
        role="group"
        aria-label="Быстрый период"
        sx={{
          flexWrap: "wrap",
          gap: 1,
          justifyContent: { xs: "flex-start", lg: "flex-end" },
        }}
      >
        {OPTIONS.map((preset) => {
          const selected = period.preset === preset;
          return (
            <Chip
              key={preset}
              label={PERIOD_PRESET_LABELS[preset]}
              size="small"
              variant="filled"
              color={selected ? "primary" : "default"}
              aria-pressed={selected}
              onClick={(event) => select(preset, event)}
            />
          );
        })}
      </Stack>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <DatePicker
              label="С"
              timezone="UTC"
              format="DD.MM.YYYY"
              value={moment.utc(draft.from)}
              maxDate={moment.utc(draft.to)}
              onChange={changeDay("from")}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label="По"
              timezone="UTC"
              format="DD.MM.YYYY"
              value={moment.utc(draft.to)}
              minDate={moment.utc(draft.from)}
              onChange={changeDay("to")}
              slotProps={{ textField: { size: "small" } }}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "flex-end" }}
          >
            <Button onClick={close}>Отмена</Button>
            <Button variant="contained" onClick={apply}>
              Применить
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
